import { useRef } from "react";
import AjusteCard from "./AjusteCard";
import {
  FaTimes,
  FaWhatsapp,
  FaDownload,
  FaCheckCircle,
  FaSpinner,
} from "react-icons/fa";
import { formatMonto } from "../../utils/rentalCalculations";
import { useWhatsappImageShare } from "../../hooks/useWhatsappImageShare";
import ModalPortal from "../ModalPortal";

const MESES_FULL = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const mesNombreCompleto = (iso) => {
  if (!iso) return "";
  const [y, m] = iso.split("-");
  const idx = Number(m) - 1;
  if (idx < 0 || idx > 11) return "";
  return `${MESES_FULL[idx]} ${y}`;
};

const slugify = (str) =>
  String(str || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

function AjusteShareModal({ data, onClose }) {
  const cardRef = useRef(null);

  const contrato = data?.contrato;
  const fileName = `Ajuste-${slugify(contrato?.locatarioNombre)}-${data?.fechaAumento || ""}.png`;

  // Mensaje usado como caption en el compartir nativo (celular).
  const mensaje = data
    ? [
        `Hola${contrato?.locatarioNombre ? ` ${contrato.locatarioNombre.split(" ")[0]}` : ""}!`,
        ``,
        `Te paso el ajuste de tu alquiler en ${contrato?.propiedad}.`,
        ``,
        `Desde ${mesNombreCompleto(data.fechaAumento)} el monto es ${formatMonto(
          data.montoNuevo,
          contrato?.moneda
        )} (+${Number(data.porcentaje).toFixed(2)}%).`,
        ``,
        `Cualquier consulta nos avisas.`,
        `Inmobiliaria Plorutti`,
      ].join("\n")
    : "";

  const { generating, feedback, waReady, abrirWhatsapp, descargar, compartir } = useWhatsappImageShare({
    cardRef,
    fileName,
    telefono: contrato?.telefono,
    caption: mensaje,
  });

  if (!data) return null;

  return (
    <ModalPortal>
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4 py-6 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg my-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 inline-flex items-center gap-2">
            <FaCheckCircle className="text-green-500" /> Aumento aplicado
          </h3>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <FaTimes />
          </button>
        </div>

        <div className="p-4 max-h-[70vh] overflow-y-auto bg-gray-50 dark:bg-gray-900/30">
          <div className="flex justify-center">
            <AjusteCard ref={cardRef} data={data} />
          </div>
        </div>

        {feedback && (
          <div
            className={`mx-4 mb-2 mt-3 rounded-lg px-3 py-2 text-xs ${
              feedback.type === "success"
                ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400"
                : feedback.type === "info"
                ? "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400"
                : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
            }`}
          >
            {feedback.msg}
          </div>
        )}

        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
          >
            Cerrar
          </button>
          <button
            onClick={descargar}
            disabled={generating}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg disabled:opacity-50"
          >
            {generating ? (
              <FaSpinner className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <FaDownload className="w-3.5 h-3.5" />
            )}
            Descargar
          </button>
          {waReady ? (
            <button
              onClick={abrirWhatsapp}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg"
              title="Abre la app de WhatsApp o, si no está, WhatsApp Web"
            >
              <FaWhatsapp className="w-4 h-4" /> Abrir WhatsApp
            </button>
          ) : (
            <button
              onClick={compartir}
              disabled={generating}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg disabled:opacity-50"
              title="Compartir el ajuste por WhatsApp (adjunta o copia la imagen)"
            >
              {generating ? (
                <FaSpinner className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <FaWhatsapp className="w-4 h-4" />
              )}
              WhatsApp
            </button>
          )}
        </div>
      </div>
    </div>
    </ModalPortal>
  );
}

export default AjusteShareModal;
